import type { Column, Row, Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { Download, FileSpreadsheet, FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { columnLabel } from '@/components/ColumnPicker'
import { exportToCSV, exportToExcel } from '@/utils/importExport'

interface TableDownloadMenuProps<TData> {
  table: Table<TData>
  /** Base filename, without extension or date. */
  filename: string
  /** The rendered table, to read display-only columns off their cells. */
  getTableElement?: () => HTMLTableElement | null
}

/** A cell's text as it reads on screen, its lines joined on one line. */
function cellText(cell: Element | null | undefined): string {
  const text = (cell as HTMLElement | null)?.innerText ?? ''
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' · ')
}

/**
 * Downloads a list as it stands: the rows it shows, in their order, under
 * every column that is switched on. A column gives its `exportValue`, else its
 * field, else — for a display-only column such as a status or a timeline —
 * the text its cell shows. The selection checkbox, columns that opt out, and
 * display-only columns with nothing to read, such as action buttons, are left out.
 */
export function TableDownloadMenu<TData>({ table, filename, getTableElement }: TableDownloadMenuProps<TData>) {
  const { t } = useTranslation()

  const exportColumns = () =>
    table
      .getVisibleLeafColumns()
      .filter((c) => c.id !== 'select' && c.columnDef.meta?.export !== false)
  const hasTotals = exportColumns().some((c) => c.columnDef.meta?.exportTotal !== undefined)

  const rowsToExport = () => {
    const tableElement = getTableElement?.()
    const valueOf = (row: Row<TData>, column: Column<TData, unknown>): unknown => {
      const exportValue = column.columnDef.meta?.exportValue
      if (exportValue) return exportValue(row.original)
      if (column.accessorFn) return row.getValue(column.id)
      const rowElement = tableElement?.querySelector(`tr[data-row-id="${CSS.escape(row.id)}"]`)
      return cellText(rowElement?.querySelector(`td[data-column-id="${CSS.escape(column.id)}"]`))
    }

    const rows = table.getRowModel().flatRows
    const values = exportColumns().map((column) => ({
      column,
      values: rows.map((row) => valueOf(row, column)),
    }))
    // A display-only column that reads empty throughout holds only icons or buttons.
    const columns = values.filter(
      ({ column, values }) =>
        !!column.accessorFn ||
        !!column.columnDef.meta?.exportValue ||
        values.some((v) => v !== '' && v != null)
    )
    const hasIdColumn = columns.some(({ column }) => column.id === 'id')
    return rows.map((row, i) => {
      const out: Record<string, unknown> = {}
      const id = (row.original as { id?: unknown }).id
      if (!hasIdColumn && typeof id === 'string') out[t('common.id')] = id
      for (const { column, values } of columns) out[columnLabel(column)] = values[i]
      return out
    })
  }

  /** The footer totals, labelled in the first column that has none of its own. */
  const totalsToExport = (firstKey: string | undefined) => {
    const out: Record<string, unknown> = {}
    for (const column of exportColumns()) {
      const total = column.columnDef.meta?.exportTotal
      if (total !== undefined) out[columnLabel(column)] = total
    }
    if (firstKey && !(firstKey in out)) out[firstKey] = t('importExport.total')
    return out
  }

  const download = (format: 'csv' | 'excel' | 'excelTotals') => {
    const name = `${filename}-${new Date().toISOString().slice(0, 10)}`
    const rows = rowsToExport()
    if (format === 'csv') exportToCSV(rows, name)
    else if (format === 'excel') void exportToExcel(rows, name)
    else {
      const totals = totalsToExport(Object.keys(rows[0] ?? {})[0])
      void exportToExcel(rows, name, {}, undefined, undefined, totals)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('importExport.download')}
          title={t('importExport.download')}
          disabled={!table.getRowModel().rows.length}
        >
          <Download className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => download('excel')}>
          <FileSpreadsheet className="size-4" />
          {t('importExport.downloadExcel')}
        </DropdownMenuItem>
        {hasTotals && (
          <DropdownMenuItem onClick={() => download('excelTotals')}>
            <FileSpreadsheet className="size-4" />
            {t('importExport.downloadExcelTotals')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => download('csv')}>
          <FileText className="size-4" />
          {t('importExport.downloadCSV')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
